import React, { useState, useEffect } from 'react';
import { Button } from '@mui/material';
import { GetApp, Check } from '@mui/icons-material';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export const InstallButton: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    // Simple check without excessive logging
    const checkIfInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true) {
        setIsInstalled(true);
        return true;
      }
      return false;
    };

    const handleBeforeInstallPrompt = (event: any) => {
      // console.log('PWA: beforeinstallprompt event received');

      // Always prevent the default mini-infobar
      event.preventDefault();

      // Store the event for later use
      setDeferredPrompt(event);
      setShowButton(true);

      // console.log('PWA: Install prompt captured and ready');
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowButton(false);
      setDeferredPrompt(null);
    };

    checkIfInstalled();

    // Add comprehensive PWA debugging
    // console.log('PWA Debug: Setting up install button...');
    // console.log('PWA Debug: Service Worker support:', 'serviceWorker' in navigator);
    // console.log('PWA Debug: Current protocol:', window.location.protocol);
    // console.log('PWA Debug: Is secure context:', window.isSecureContext);

    // Check if manifest exists
    // const manifestLink = document.querySelector('link[rel="manifest"]');
    // console.log('PWA Debug: Manifest link found:', !!manifestLink);

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Force a check after some time (dev debugging)
    // setTimeout(() => {
    //   console.log('PWA Debug: Delayed check - Install button visible?', showButton);
    //   if ('serviceWorker' in navigator) {
    //     navigator.serviceWorker.getRegistrations().then(regs => {
    //       console.log('PWA Debug: Service Worker registrations:', regs.length);
    //       regs.forEach(reg => console.log('PWA Debug: SW state:', reg.active?.state));
    //     });
    //   }
    // }, 5000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // console.log('PWA: No deferred prompt available');
      return;
    }

    try {
      // console.log('PWA: Triggering install prompt...');

      // Try to show the install prompt
      if (deferredPrompt.prompt) {
        await deferredPrompt.prompt();

        // Wait for the user choice
        if (deferredPrompt.userChoice) {
          const { outcome } = await deferredPrompt.userChoice;
          // console.log(`PWA: User ${outcome} the install prompt`);

          if (outcome === 'accepted') {
            setIsInstalled(true);
          }
        }
      }
    } catch (error) {
      console.error('PWA: Install prompt error:', error);
    } finally {
      // Clear the deferred prompt since it can only be used once
      setDeferredPrompt(null);
      setShowButton(false);
    }
  };

  // Don't show anything if installed or if install prompt is not available
  if (isInstalled || !showButton) {
    return null;
  }

  return (
    <Button
      variant="contained"
      startIcon={<GetApp />}
      onClick={handleInstallClick}
      sx={{
        minWidth: 120,
        textTransform: 'none',
        backgroundColor: 'var(--primary-color)',
        '&:hover': {
          backgroundColor: 'var(--secondary-color)'
        }
      }}
    >
      Install App
    </Button>
  );
};